import { useEffect, useMemo, useState } from 'react';
import type { TesterPresenceItem } from '@runhuman/shared';
import {
  usePublicTesterProfile,
  usePublicTesterSchedule,
} from '../../../use-tester-profile';
import { Header } from './Header';
import { MainTab } from './MainTab';
import { AboutTab } from './AboutTab';
import { HistoryTab } from './HistoryTab';

type TabId = 'main' | 'about' | 'history';

export interface InternalTesterProfileDialogProps {
  /** The tester whose profile is open. `null` closes the dialog. */
  readonly tester: TesterPresenceItem | null;
  readonly accessToken: string;
  /** Drives the History tab — non-admins never see the dialog at all. */
  readonly isAdmin: boolean;
  readonly onClose: () => void;
}

/**
 * In-Activity counterpart to `<InternalTesterProfileDialog>` from the web
 * Tester Portal (#3967). Same Main / About / (admin-only) History tabs,
 * same data sources — just rendered with the Activity's CSS classes
 * (`profile-modal__*`) and bound to the Activity's Discord-OAuth-guarded
 * routes via the `use-tester-profile` hooks.
 *
 * Mounted at the `<TesterView>` level so it overlays the queue + presence
 * surface; clicks on the backdrop close it the same way the Discord SDK
 * dismisses any modal.
 */
export function InternalTesterProfileDialog({
  tester,
  accessToken,
  isAdmin,
  onClose,
}: InternalTesterProfileDialogProps) {
  const [tab, setTab] = useState<TabId>('main');
  const isOpen = tester !== null;
  const { startDate, endDate } = useMemo(scheduleRange, []);

  const profileQuery = usePublicTesterProfile(accessToken, tester?.id ?? null);
  const scheduleQuery = usePublicTesterSchedule(
    accessToken,
    tester?.id ?? null,
    startDate,
    endDate,
  );

  // Reset to the Main tab whenever a new tester is opened so consecutive
  // opens don't bleed the previous tab selection across distinct profiles.
  useEffect(() => {
    if (isOpen) setTab('main');
  }, [tester?.id, isOpen]);

  if (!isOpen || !tester) return null;

  return (
    <div
      className="profile-modal__backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="profile-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${tester.alias} — tester profile`}
      >
        <button
          type="button"
          className="profile-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {profileQuery.isLoading && (
          <div className="profile-modal__loading">Loading profile…</div>
        )}
        {profileQuery.isError && (
          <div className="profile-modal__error">
            {(profileQuery.error as Error).message}
          </div>
        )}
        {profileQuery.data && (
          <>
            <Header profile={profileQuery.data} liveAvailability={tester.availability} />
            <Tabs active={tab} onSelect={setTab} showHistory={isAdmin} />
            <div className="profile-modal__panel">
              {tab === 'main' && (
                <MainTab
                  schedule={scheduleQuery.data}
                  scheduleLoading={scheduleQuery.isLoading}
                  tester={tester}
                />
              )}
              {tab === 'about' && <AboutTab profile={profileQuery.data} />}
              {tab === 'history' && isAdmin && (
                <HistoryTab accessToken={accessToken} testerId={tester.id} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Tabs({
  active,
  onSelect,
  showHistory,
}: {
  active: TabId;
  onSelect: (tab: TabId) => void;
  showHistory: boolean;
}) {
  return (
    <div role="tablist" className="profile-modal__tabs">
      <TabButton id="main" label="Main" active={active === 'main'} onSelect={onSelect} />
      <TabButton id="about" label="About" active={active === 'about'} onSelect={onSelect} />
      {showHistory && (
        <TabButton id="history" label="History" active={active === 'history'} onSelect={onSelect} />
      )}
    </div>
  );
}

function TabButton({
  id,
  label,
  active,
  onSelect,
}: {
  id: TabId;
  label: string;
  active: boolean;
  onSelect: (tab: TabId) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`profile-modal__tab${active ? ' profile-modal__tab--active' : ''}`}
      onClick={() => onSelect(id)}
    >
      {label}
    </button>
  );
}

function scheduleRange(): { startDate: string; endDate: string } {
  // Same 7-day window the web dialog uses so the Schedule strip data shape
  // matches when rendered in either surface.
  const now = new Date();
  const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return { startDate: toUtcDate(now), endDate: toUtcDate(end) };
}

function toUtcDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
